import {
  ConversationMessage,
  SmartSearchBotResponse,
  ToolCallParameters,
} from '@ltvco/refresh-lib/api';
import { unformatPhone } from '@ltvco/refresh-lib/utils';
import { Record as TeaserRecord } from '@ltvco/refresh-lib/v1';
import { fetchReportByPermalinkInLine } from '@ltvco/refresh-lib/v2';
import OpenAI from 'openai';

import {
  DETECT_IMAGE_REPORTS,
  FUNCTION_CALL_OUTPUT,
  FUNCTION_CALL_TYPE,
  GENERIC_ERROR_MESSAGE,
  MESSAGE,
  NOT_SUPPORTED_SEARCH_TYPE,
  OUTPUT_TEXT,
  REPORT_PROCESSORS,
  SEARCH_TYPES,
  TRIGGER_SEARCH,
  UNSUPPORTED_SEARCH_ERROR_MESSAGE,
  UNSUPPORTED_SEARCH_TYPE_ERROR_MESSAGE,
  CUSTOMER_SERVICE_CONTACT,
  ROLES,
  USE_OPENAI_LOCAL_CONFIGURATION,
  GET_SPECIFIC_REPORT_SECTION,
  NULL_POSSIBLE_REPORT_PARAMS,
} from 'components/ChatBV/constants';
import {
  GenericLLMResponse,
  ProcessedFunctionCallResponse,
  PersonParamsFunctionArguments,
  GenericReportResponse,
  FunctionCallResponse,
  PermalinkOrTeaserOrParamsResult,
  BaseMessage,
  GenericLLMRequest,
  LLMRequestItem,
  InputItem,
  LLMFunctionCallOutput,
  ReponsesAPIRequestProps,
  GetSpecificReportSectionFunctionArguments,
  DetectImageReportsFunctionArguments,
  FlatPersonReport,
  GeneratedReport,
  FlatVehicleReport,
  PossibleReportParams,
  Message,
  FlatUsernameReport,
} from 'components/ChatBV/types';
import {
  getPhoneNumberPermalink,
  getEmailPermalink,
  getPropertyPermalink,
  getVehiclePermalink,
  getPublicRecordsTeaser,
  isUnknown,
  flatReportDataBySearchType,
  getUsernamePermalink,
} from 'components/ChatBV/utils';

import {
  FlatEmailReport,
  FlatPhoneReport,
  FlatPropertyReport,
} from '../types/DeepDiveReportType';

export const mapLLMServiceResponseToGenericLLMResponse = (
  response: SmartSearchBotResponse
): GenericLLMResponse => {
  if (
    Array.isArray(response.data?.tool_calls) &&
    response.data.tool_calls.length > 0
  ) {
    const toolCalls = response.data.tool_calls;
    const functionCalls: FunctionCallResponse[] = toolCalls.map((toolCall) => ({
      call_id: toolCall.call_id,
      name: toolCall.tool || '',
      arguments: toolCall.parameters as
        | GetSpecificReportSectionFunctionArguments
        | ToolCallParameters
        | null,
      type: 'function_call',
      status: 'completed',
    }));

    return {
      context_id: response.context_id,
      data: [null, functionCalls],
      response_id: response.response_id,
    };
  }

  // Extract annotations from response.data if they exist
  const annotations = (response.data as any)?.annotations || [];

  return {
    context_id: response.context_id,
    data: [response.data?.answer ?? '', null],
    response_id: response.response_id,
    sources: annotations,
  };
};

/** Maps API search_type to canonical SEARCH_TYPES. Normalizes casing/underscores then maps aliases (e.g. family_search → ancestor, professional → contact). Export for use in useTeaserLoader. */
export const normalizeSearchType = (searchType: string): string => {
  const normalized = searchType.toLowerCase().replace(/_/g, '');
  const map: Record<string, string> = {
    familysearch: SEARCH_TYPES.ANCESTOR,
    genealogy: SEARCH_TYPES.ANCESTOR,
    professional: SEARCH_TYPES.CONTACT,
    courtcases: SEARCH_TYPES.COURTCASES,
    courtopinions: SEARCH_TYPES.COURT_OPINIONS,
    person: SEARCH_TYPES.PERSON,
    publicrecords: SEARCH_TYPES.PERSON,
    obituary: SEARCH_TYPES.OBITUARY,
    obituaries: SEARCH_TYPES.OBITUARY,
    contact: SEARCH_TYPES.CONTACT,
    contacts: SEARCH_TYPES.CONTACT,
    ancestor: SEARCH_TYPES.ANCESTOR,
  };
  return map[normalized] ?? normalized;
};

const determineSearchType = (args: ToolCallParameters) => {
  if (args.search_type) {
    return normalizeSearchType(args.search_type);
  }
  if (args.phone_number) {
    return SEARCH_TYPES.PHONE;
  }
  if (args.email) {
    return SEARCH_TYPES.EMAIL;
  }
  if (args.username) {
    return SEARCH_TYPES.USERNAME;
  }
  if (args.address && args.city && args.state && args.zipcode) {
    return SEARCH_TYPES.PROPERTY;
  }
  if (
    (args.vehicle_year &&
      args.vehicle_make &&
      args.vehicle_model &&
      args.vehicle_trim) ||
    args.vehicle_vin ||
    (args.vehicle_license_plate && args.state)
  ) {
    return SEARCH_TYPES.VEHICLE;
  }
  if (args.first_name && args.last_name) {
    return SEARCH_TYPES.PERSON;
  }
  return NOT_SUPPORTED_SEARCH_TYPE;
};

const sanitizeToFiveDigitZip = (zipCodeString: string) => {
  const regex = /\b(\d{5})-(\d{4})\b/;

  const convertedAddress = zipCodeString.replace(regex, (_, p1) => p1);

  return convertedAddress;
};
/** Optional dependencies for handleFunctionCalls (e.g. court opinions search when running in browser with token) */
/** Params for ancestor/genealogy search (maps to AdvancedSearchURLParams at call site) */
export interface AncestorSearchParams {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  age?: string;
  state?: string;
  city?: string;
  birth_month?: string;
  birth_year?: string;
  death_month?: string;
  death_year?: string;
}

/** Optional dependencies for handleFunctionCalls (e.g. court search when running in browser with token) */
export interface HandleFunctionCallsOptions {
  searchCourtOpinions?: (
    query: string
  ) => Promise<{ results: unknown[]; count: number }>;
  searchCourtCases?: (
    query: string
  ) => Promise<{ results: unknown[]; count: number }>;
  searchAncestor?: (
    params: AncestorSearchParams
  ) => Promise<{ results: unknown[]; count: number } | undefined>;
  searchObituary?: (
    params: ObituaryTeaserParamsForHandler
  ) => Promise<{ results: unknown[]; count: number } | undefined>;
  searchContact?: (
    params: ContactTeaserParamsForHandler
  ) => Promise<{ results: unknown[]; count: number }>;
}

/** Params for obituary search in the chat handler (name required; dob, dod, location optional) */
export interface ObituaryTeaserParamsForHandler {
  name: string;
  dob?: string | null;
  dod?: string | null;
  location?: string | null;
}

/** Params for professional/contact search in the chat handler */
export interface ContactTeaserParamsForHandler {
  name: string;
  title?: string | null;
  company?: string | null;
  city?: string | null;
  state?: string | null;
  industry?: string | null;
  level?: string | null;
}

/** Treat null, undefined, empty string, and the literal string "null" as empty (LLM sometimes returns "null" as text). */
function isEmptyOrNullString(v: unknown): boolean {
  if (v === null || v === undefined) {
    return true;
  }
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '' || s === 'null' || s === 'unknown';
  }
  return false;
}

export const handleFunctionCalls = async (
  functionCalls: FunctionCallResponse[],
  generatedReports: Record<string, GeneratedReport>,
  options?: HandleFunctionCallsOptions
): Promise<ProcessedFunctionCallResponse[]> => {
  const processedFunctionCalls: ProcessedFunctionCallResponse[] = [];
  const handlerResultCache = new Map<string, PermalinkOrTeaserOrParamsResult>();

  for (const functionCall of functionCalls) {
    if (functionCall.arguments && functionCall.name) {
      if (functionCall.name === TRIGGER_SEARCH) {
        const searchArguments = functionCall.arguments as ToolCallParameters;
        const search_type = determineSearchType(searchArguments);
        const callId = functionCall.call_id;

        if (search_type === NOT_SUPPORTED_SEARCH_TYPE) {
          processedFunctionCalls.push(
            createFunctionCallResultForTriggerSearch(
              functionCall.call_id,
              UNSUPPORTED_SEARCH_TYPE_ERROR_MESSAGE,
              null,
              search_type,
              null
            )
          );
        } else {
          try {
            const searchHandlers = {
              [SEARCH_TYPES.PHONE]: async () => {
                const { phone_number } = searchArguments;
                if (phone_number) {
                  let sanitizedPhoneNumber = unformatPhone(phone_number);
                  if (
                    sanitizedPhoneNumber.length === 11 &&
                    sanitizedPhoneNumber.startsWith('1')
                  ) {
                    sanitizedPhoneNumber = sanitizedPhoneNumber.substring(1);
                  }
                  if (sanitizedPhoneNumber.length !== 10) {
                    return {
                      error: 'Phone number must be 10 digits',
                    };
                  }
                  return await getPhoneNumberPermalink(sanitizedPhoneNumber);
                }
                return {
                  error: 'Phone number is required',
                };
              },
              [SEARCH_TYPES.USERNAME]: async () => {
                const { username } = searchArguments;
                if (username) {
                  return await getUsernamePermalink(username);
                }
                return {
                  error: 'Username is required',
                };
              },
              [SEARCH_TYPES.EMAIL]: async () => {
                const { email } = searchArguments;
                if (email) {
                  return await getEmailPermalink(email);
                }
                return {
                  error: 'Email is required',
                };
              },
              [SEARCH_TYPES.PROPERTY]: async () => {
                const { address, city, state, zipcode } = searchArguments;
                if (
                  !address ||
                  !city ||
                  !state ||
                  !zipcode ||
                  isUnknown(address) ||
                  isUnknown(city) ||
                  isUnknown(state) ||
                  isUnknown(zipcode)
                ) {
                  const missingParams = [];
                  if (!address || isUnknown(address)) {
                    missingParams.push('street address');
                  }
                  if (!city || isUnknown(city)) {
                    missingParams.push('city');
                  }
                  if (!state || isUnknown(state)) {
                    missingParams.push('state');
                  }
                  if (!zipcode || isUnknown(zipcode)) {
                    missingParams.push('zipcode');
                  }
                  return {
                    error: `Missing params: ${missingParams.join(', ')}`,
                  };
                }
                return await getPropertyPermalink(
                  sanitizeToFiveDigitZip(address),
                  city,
                  state,
                  zipcode
                );
              },
              [SEARCH_TYPES.VEHICLE]: async () => {
                const {
                  vehicle_year,
                  vehicle_make,
                  vehicle_model,
                  vehicle_trim,
                  vehicle_vin,
                  vehicle_license_plate,
                  state,
                } = searchArguments;
                return await getVehiclePermalink({
                  state,
                  year: vehicle_year,
                  make: vehicle_make,
                  model: vehicle_model,
                  trim: vehicle_trim,
                  vin: vehicle_vin,
                  licensePlate: vehicle_license_plate,
                });
              },
              [SEARCH_TYPES.PERSON]: async () => {
                const {
                  first_name,
                  last_name,
                  middle_name,
                  age,
                  state,
                  city,
                  title,
                  company,
                  industry,
                  level,
                  birth_month,
                  birth_year,
                  death_month,
                  death_year,
                } = searchArguments;
                if (
                  !first_name ||
                  !last_name ||
                  isUnknown(first_name) ||
                  isUnknown(last_name)
                ) {
                  const missingParams = [];
                  if (!first_name || isUnknown(first_name)) {
                    missingParams.push('first name');
                  }
                  if (!last_name || isUnknown(last_name)) {
                    missingParams.push('last name');
                  }
                  return {
                    error: `Missing params: ${missingParams.join(', ')}`,
                  };
                }
                const teaserResult = await getPublicRecordsTeaser({
                  first_name,
                  last_name,
                  middle_name: middle_name ?? '',
                  age: age ?? '',
                  state: state ?? '',
                  city: city ?? '',
                });
                return {
                  teaser: teaserResult,
                  params: {
                    first_name,
                    last_name,
                    middle_name: middle_name ?? '',
                    age: age ?? '',
                    state: state ?? '',
                    city: city ?? '',
                    title: title ?? '',
                    company: company ?? '',
                    industry: industry ?? '',
                    level: level ?? '',
                    birth_month: birth_month ?? '',
                    birth_year: birth_year ?? '',
                    death_month: death_month ?? '',
                    death_year: death_year ?? '',
                  },
                };
              },
              // Teaser-only types: same name params, no permalink; UI Fetchers load results by collection type
              [SEARCH_TYPES.OBITUARY]: async () => {
                const {
                  first_name,
                  last_name,
                  middle_name,
                  age,
                  state,
                  city,
                  birth_month,
                  birth_year,
                  death_month,
                  death_year,
                } = searchArguments;
                if (
                  !first_name ||
                  !last_name ||
                  isUnknown(first_name) ||
                  isUnknown(last_name)
                ) {
                  return {
                    error:
                      'First name and last name are required for obituary search.',
                  };
                }
                const name = [first_name, middle_name, last_name]
                  .filter((part) => !isEmptyOrNullString(part))
                  .join(' ')
                  .trim();
                let teaserData: TeaserRecord[] = [];
                let amountOfResults = 0;
                if (options?.searchObituary && name) {
                  try {
                    // Ignore "null" and "unknown" when building dates (searchbot may return "unknown" or "death month unknown")
                    const isMissingDatePart = (v: unknown) =>
                      isEmptyOrNullString(v) ||
                      (typeof v === 'string' &&
                        (v.trim().toLowerCase().includes('unknown') ||
                          v.trim().toLowerCase().includes('null')));
                    const dob =
                      !isMissingDatePart(birth_month) &&
                      !isMissingDatePart(birth_year)
                        ? `${String(birth_year)}-${String(birth_month).padStart(
                            2,
                            '0'
                          )}`
                        : undefined;
                    // dod: full "YYYY-MM" when both valid, or only year when just death_year is valid
                    const dod =
                      !isMissingDatePart(death_year) &&
                      !isMissingDatePart(death_month)
                        ? `${String(death_year)}-${String(death_month).padStart(
                            2,
                            '0'
                          )}`
                        : !isMissingDatePart(death_year)
                        ? String(death_year)
                        : undefined;
                    const locationParts = [city, state].filter(
                      (part) => !isEmptyOrNullString(part)
                    );
                    const location =
                      locationParts.length > 0
                        ? locationParts.join(', ').trim()
                        : undefined;
                    const response = await options.searchObituary({
                      name,
                      ...(dob !== undefined && dob !== '' && { dob }),
                      ...(dod !== undefined && dod !== '' && { dod }),
                      ...(location !== undefined &&
                        location !== '' && { location }),
                    });
                    if (response?.results?.length) {
                      teaserData = response.results as TeaserRecord[];
                      amountOfResults =
                        response.count ?? response.results.length;
                    }
                  } catch {
                    // Keep empty teaser on error
                  }
                }
                return {
                  teaser: { teaserData, amountOfResults },
                  params: {
                    first_name: first_name ?? '',
                    last_name: last_name ?? '',
                    middle_name: middle_name ?? '',
                    age: age ?? '',
                    state: state ?? '',
                    city: city ?? '',
                    title: '',
                    company: '',
                    industry: '',
                    level: '',
                    birth_month: birth_month ?? '',
                    birth_year: birth_year ?? '',
                    death_month: death_month ?? '',
                    death_year: death_year ?? '',
                  },
                };
              },
              [SEARCH_TYPES.ANCESTOR]: async () => {
                const {
                  first_name,
                  last_name,
                  middle_name,
                  age,
                  state,
                  city,
                  birth_month,
                  birth_year,
                  death_month,
                  death_year,
                } = searchArguments;
                if (
                  !first_name ||
                  !last_name ||
                  isUnknown(first_name) ||
                  isUnknown(last_name)
                ) {
                  return {
                    error:
                      'First name and last name are required for genealogy search.',
                  };
                }
                const params = {
                  first_name: first_name ?? '',
                  last_name: last_name ?? '',
                  middle_name: middle_name ?? '',
                  age: age ?? '',
                  state: state ?? '',
                  city: city ?? '',
                  birth_month: birth_month ?? '',
                  birth_year: birth_year ?? '',
                  death_month: death_month ?? '',
                  death_year: death_year ?? '',
                };
                let teaserData: TeaserRecord[] = [];
                let amountOfResults = 0;
                if (options?.searchAncestor) {
                  try {
                    const response = await options.searchAncestor(params);
                    if (response?.results?.length) {
                      teaserData = response.results as TeaserRecord[];
                      amountOfResults =
                        response.count ?? response.results.length;
                    }
                  } catch {
                    // Keep empty teaser on error
                  }
                }
                return {
                  teaser: { teaserData, amountOfResults },
                  params: {
                    ...params,
                    title: '',
                    company: '',
                    industry: '',
                    level: '',
                  },
                };
              },
              [SEARCH_TYPES.CONTACT]: async () => {
                const {
                  first_name,
                  last_name,
                  middle_name,
                  state,
                  city,
                  title,
                  company,
                  industry,
                  level,
                } = searchArguments;
                if (
                  !first_name ||
                  !last_name ||
                  isUnknown(first_name) ||
                  isUnknown(last_name)
                ) {
                  return {
                    error:
                      'First name and last name are required for professional contact search.',
                  };
                }
                const name = [first_name, middle_name, last_name]
                  .filter((part) => !isEmptyOrNullString(part))
                  .join(' ')
                  .trim();
                let teaserData: TeaserRecord[] = [];
                let amountOfResults = 0;
                if (options?.searchContact && name) {
                  try {
                    const response = await options.searchContact({
                      name,
                      title: title ?? null,
                      company: company ?? null,
                      city: city ?? null,
                      state: state ?? null,
                      industry: industry ?? null,
                      level: level ?? null,
                    });
                    if (response?.results?.length) {
                      teaserData = response.results as TeaserRecord[];
                      amountOfResults =
                        response.count ?? response.results.length;
                    }
                  } catch {
                    // Keep empty teaser on error
                  }
                }
                return {
                  teaser: { teaserData, amountOfResults },
                  params: {
                    first_name: first_name ?? '',
                    last_name: last_name ?? '',
                    middle_name: middle_name ?? '',
                    age: searchArguments.age ?? '',
                    state: state ?? '',
                    city: city ?? '',
                    title: title ?? '',
                    company: company ?? '',
                    industry: industry ?? '',
                    level: level ?? '',
                    birth_month: '',
                    birth_year: '',
                    death_month: '',
                    death_year: '',
                  },
                };
              },
              [SEARCH_TYPES.COURTCASES]: async () => {
                const {
                  case_name,
                  first_name,
                  last_name,
                  middle_name,
                  age,
                  state,
                  city,
                } = searchArguments;
                const hasCaseName = case_name && !isUnknown(case_name);
                const hasName =
                  first_name &&
                  last_name &&
                  !isUnknown(first_name) &&
                  !isUnknown(last_name);
                if (!hasCaseName && !hasName) {
                  return {
                    error:
                      'Case name or first name and last name are required for court cases search.',
                  };
                }
                const query =
                  [first_name, middle_name, last_name]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                  case_name ||
                  '';
                let teaserData: TeaserRecord[] = [];
                let amountOfResults = 0;
                if (options?.searchCourtCases && query.trim()) {
                  try {
                    const response = await options.searchCourtCases(
                      query.trim()
                    );
                    if (response?.results?.length) {
                      teaserData = response.results as TeaserRecord[];
                      amountOfResults =
                        response.count ?? response.results.length;
                    }
                  } catch {
                    // Keep empty teaser on error
                  }
                }
                return {
                  teaser: { teaserData, amountOfResults },
                  params: {
                    case_name: case_name ?? '',
                    first_name: first_name ?? '',
                    last_name: last_name ?? '',
                    middle_name: middle_name ?? '',
                    age: age ?? '',
                    state: state ?? '',
                    city: city ?? '',
                    title: '',
                    company: '',
                    industry: '',
                    level: '',
                    birth_month: '',
                    birth_year: '',
                    death_month: '',
                    death_year: '',
                  },
                };
              },
              [SEARCH_TYPES.COURT_OPINIONS]: async () => {
                const {
                  case_name,
                  first_name,
                  last_name,
                  middle_name,
                  age,
                  state,
                  city,
                } = searchArguments;
                const hasCaseName = case_name && !isUnknown(case_name);
                const hasName =
                  first_name &&
                  last_name &&
                  !isUnknown(first_name) &&
                  !isUnknown(last_name);
                if (!hasCaseName && !hasName) {
                  return {
                    error:
                      'Case name or first name and last name are required for court opinions search.',
                  };
                }
                const query =
                  [first_name, middle_name, last_name]
                    .filter(Boolean)
                    .join(' ')
                    .trim() ||
                  case_name ||
                  '';
                let teaserData: TeaserRecord[] = [];
                let amountOfResults = 0;
                if (options?.searchCourtOpinions && query.trim()) {
                  try {
                    const response = await options.searchCourtOpinions(
                      query.trim()
                    );
                    if (response?.results?.length) {
                      teaserData = response.results as TeaserRecord[];
                      amountOfResults =
                        response.count ?? response.results.length;
                    }
                  } catch {
                    // Keep empty teaser on error
                  }
                }
                return {
                  teaser: { teaserData, amountOfResults },
                  params: {
                    case_name: case_name ?? '',
                    first_name: first_name ?? '',
                    last_name: last_name ?? '',
                    middle_name: middle_name ?? '',
                    age: age ?? '',
                    state: state ?? '',
                    city: city ?? '',
                    title: '',
                    company: '',
                    industry: '',
                    level: '',
                    birth_month: '',
                    birth_year: '',
                    death_month: '',
                    death_year: '',
                  },
                };
              },
            };

            const handler = searchHandlers[search_type];
            const cacheKey = `${search_type}-${JSON.stringify(
              searchArguments
            )}`;
            let permalinkOrTeaserOrParamsResult: PermalinkOrTeaserOrParamsResult;
            if (handlerResultCache.has(cacheKey)) {
              permalinkOrTeaserOrParamsResult = handlerResultCache.get(
                cacheKey
              ) as PermalinkOrTeaserOrParamsResult;
            } else {
              permalinkOrTeaserOrParamsResult = handler
                ? await handler()
                : { permalink: undefined };
              handlerResultCache.set(cacheKey, permalinkOrTeaserOrParamsResult);
            }

            if (permalinkOrTeaserOrParamsResult.permalink) {
              // for all report types that are not person search
              const { data, rawData } = await fetchReportByPermalink(
                permalinkOrTeaserOrParamsResult.permalink || '',
                search_type
              );

              let processedData = data;
              if (
                (search_type === SEARCH_TYPES.PROPERTY ||
                  search_type === SEARCH_TYPES.EMAIL ||
                  search_type === SEARCH_TYPES.VEHICLE) &&
                rawData
              ) {
                const flatReport = flatReportDataBySearchType(
                  rawData,
                  search_type
                ) as
                  | FlatPropertyReport
                  | FlatEmailReport
                  | FlatVehicleReport
                  | undefined;
                processedData = filterSectionsFromFlatReport(flatReport);
              }
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  processedData,
                  permalinkOrTeaserOrParamsResult?.permalink,
                  search_type,
                  rawData as GenericReportResponse
                )
              );
            } else if (permalinkOrTeaserOrParamsResult.teaser) {
              // for person search
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  {
                    amountOfResults:
                      permalinkOrTeaserOrParamsResult.teaser.amountOfResults,
                    city: permalinkOrTeaserOrParamsResult.params?.city || '',
                    state: permalinkOrTeaserOrParamsResult.params?.state || '',
                  },
                  null,
                  search_type,
                  null,
                  permalinkOrTeaserOrParamsResult.teaser.teaserData,
                  permalinkOrTeaserOrParamsResult.params
                    ? permalinkOrTeaserOrParamsResult.params
                    : null
                )
              );
            } else {
              // something went wrong
              processedFunctionCalls.push(
                createFunctionCallResultForTriggerSearch(
                  callId,
                  permalinkOrTeaserOrParamsResult.error,
                  null,
                  search_type,
                  null
                )
              );
            }
          } catch (error) {
            // something went wrong with the request in general
            processedFunctionCalls.push(
              createFunctionCallResultForTriggerSearch(
                callId,
                error instanceof Error ? error.message : JSON.stringify(error),
                null,
                search_type,
                null
              )
            );
          }
        }
      }

      if (functionCall.name === CUSTOMER_SERVICE_CONTACT) {
        processedFunctionCalls.push(
          createFunctionCallResultForCustomerServiceContact(
            functionCall.call_id
          )
        );
      }
      if (functionCall.name === GET_SPECIFIC_REPORT_SECTION) {
        const args =
          functionCall.arguments as GetSpecificReportSectionFunctionArguments;

        if (!args) {
          processedFunctionCalls.push(
            createFunctionCallResultForGetSpecificReportSection(
              functionCall.call_id,
              'Invalid function call arguments.',
              undefined,
              undefined,
              undefined
            )
          );
          continue;
        }

        const { permalink } = args;

        // Map API boolean parameters to report section names
        // Note: vehicle_info in API maps to vehicles_info in the report
        const sectionMapping: Record<string, string> = {
          identity_or_owner_identity_info: 'identity_or_owner_identity_info',
          family_info: 'family_info',
          email_phone_or_social_info: 'email_phone_or_social_info',
          trust_and_safety_status_info: 'trust_and_safety_status_info',
          criminal_or_legal_info: 'criminal_or_legal_info',
          property_or_address_info: 'property_or_address_info',
          vehicle_info: 'vehicles_info', // API uses singular, report uses plural
          jobs_education_or_skills_info: 'jobs_education_or_skills_info',
        };

        // Find which boolean parameter is set to true
        let reportSection: string | undefined;
        for (const [apiKey, reportKey] of Object.entries(sectionMapping)) {
          if (
            args[apiKey as keyof GetSpecificReportSectionFunctionArguments] ===
            true
          ) {
            reportSection = reportKey;
            break;
          }
        }

        if (reportSection && permalink && generatedReports[permalink]) {
          const reportToUse = generatedReports[permalink];
          const flatReportData = flatReportDataBySearchType(
            reportToUse.reportData,
            reportToUse.searchType
          );

          if (flatReportData) {
            const specificReportSectionData =
              flatReportData[
                reportSection as keyof (
                  | FlatPhoneReport
                  | FlatPersonReport
                  | FlatPropertyReport
                  | FlatVehicleReport
                  | FlatEmailReport
                )
              ];
            const reportSectionData =
              specificReportSectionData === undefined
                ? 'That information is not available in the report'
                : specificReportSectionData;
            processedFunctionCalls.push(
              createFunctionCallResultForGetSpecificReportSection(
                functionCall.call_id,
                JSON.stringify(reportSectionData),
                reportToUse.reportData,
                reportToUse.permalink,
                reportToUse.searchType
              )
            );
          } else {
            processedFunctionCalls.push(
              createFunctionCallResultForGetSpecificReportSection(
                functionCall.call_id,
                'That information is not available in the report, but you can try with (suggestions: "owners", "property", "businesses", "people", "vehicles")',
                reportToUse.reportData,
                reportToUse.permalink,
                reportToUse.searchType
              )
            );
          }
        } else {
          processedFunctionCalls.push(
            createFunctionCallResultForGetSpecificReportSection(
              functionCall.call_id,
              reportSection
                ? 'That information is not available in the report, but you can try with (suggestions: "owners", "property", "businesses", "people", "vehicles")'
                : 'Please specify exactly one report section to retrieve.',
              undefined,
              undefined,
              undefined
            )
          );
        }
      }

      if (functionCall.name === DETECT_IMAGE_REPORTS) {
        const args = (functionCall.arguments ||
          {}) as DetectImageReportsFunctionArguments;
        const possibleReports = args.possible_reports || [];

        const normalizedReports: PossibleReportParams[] = [];

        for (const report of possibleReports) {
          const hasPersonInfo = report.first_name || report.last_name;
          const hasPhoneInfo = report.phone_number;
          const hasEmailInfo = report.email;
          const hasPropertyInfo =
            report.address || report.city || report.state || report.zipcode;
          const hasVehicleInfo =
            report.vehicle_vin ||
            report.vehicle_license_plate ||
            report.vehicle_make ||
            report.vehicle_model;

          const reportTypesFound: string[] = [];
          if (hasPersonInfo) {
            reportTypesFound.push('person');
          }
          if (hasPhoneInfo) {
            reportTypesFound.push('phone');
          }
          if (hasEmailInfo) {
            reportTypesFound.push('email');
          }
          if (hasPropertyInfo) {
            reportTypesFound.push('property');
          }
          if (hasVehicleInfo) {
            reportTypesFound.push('vehicle');
          }

          if (reportTypesFound.length > 1) {
            if (hasPersonInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'person',
                first_name: report.first_name || '',
                last_name: report.last_name || '',
                middle_name: report.middle_name || '',
                age: report.age || '',
                title: report.title || '',
                company: report.company || '',
                industry: report.industry || '',
                level: report.level || '',
                birth_month: report.birth_month || '',
                birth_year: report.birth_year || '',
                death_month: report.death_month || '',
                death_year: report.death_year || '',
                city: report.city || '',
                state: report.state || '',
              });
            }

            if (hasPhoneInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'phone',
                phone_number: report.phone_number || '',
              });
            }

            if (hasEmailInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'email',
                email: report.email || '',
              });
            }

            if (hasPropertyInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'property',
                address: report.address || '',
                city: report.city || '',
                state: report.state || '',
                zipcode: report.zipcode || '',
              });
            }

            if (hasVehicleInfo) {
              normalizedReports.push({
                ...NULL_POSSIBLE_REPORT_PARAMS,
                search_type: 'vehicle',
                vehicle_vin: report.vehicle_vin || '',
                vehicle_license_plate: report.vehicle_license_plate || '',
                vehicle_year: report.vehicle_year || '',
                vehicle_make: report.vehicle_make || '',
                vehicle_model: report.vehicle_model || '',
                vehicle_trim: report.vehicle_trim || '',
              });
            }
          } else {
            normalizedReports.push({
              ...report,
              search_type: (report.search_type ||
                reportTypesFound[0] ||
                '') as PossibleReportParams['search_type'],
            } as PossibleReportParams);
          }
        }

        processedFunctionCalls.push({
          type: FUNCTION_CALL_OUTPUT,
          call_id: functionCall.call_id,
          output: JSON.stringify({ possible_reports: normalizedReports }),
          possibleReports: normalizedReports,
        });
      }
    }
  }

  return processedFunctionCalls;
};

/**
 * Checks if an object has any properties with content (recursively)
 * Used to verify if nested objects should be included
 */
const checkObjectHasContentInsideSection = (objectData: any): boolean => {
  if (!objectData || typeof objectData !== 'object') {
    return false;
  }

  const keys = Object.keys(objectData);
  for (const key of keys) {
    const objectValue = objectData[key];

    if (objectValue === undefined) {
      continue;
    }

    if (Array.isArray(objectValue)) {
      if (objectValue.length > 0) {
        return true;
      }
      continue;
    }

    if (typeof objectValue === 'object' && objectValue !== null) {
      if (checkObjectHasContentInsideSection(objectValue)) {
        return true;
      }
    } else {
      return true;
    }
  }
  return false;
};

/**
 * Gets the names of sections that have content from flat report
 * Returns permalink with its value, and a formatted message listing sections
 */
const filterSectionsFromFlatReport = (
  flatReport:
    | FlatPropertyReport
    | FlatEmailReport
    | FlatVehicleReport
    | FlatUsernameReport
    | undefined
): { permalink?: string; message: string } => {
  if (!flatReport || typeof flatReport !== 'object') {
    return { permalink: undefined, message: '' };
  }

  const sectionsWithContent: string[] = [];
  let permalinkValue: string | undefined;

  const keys = Object.keys(flatReport);

  for (const key of keys) {
    const sectionData = (flatReport as any)[key];

    // Skip if the section is undefined (meaning it has no content)
    if (sectionData === undefined) {
      continue;
    }

    // Include permalink with its value
    if (key === 'permalink') {
      permalinkValue = sectionData;
      continue;
    }

    if (Array.isArray(sectionData)) {
      if (sectionData.length > 0) {
        sectionsWithContent.push(key);
      }
      continue;
    }

    if (typeof sectionData === 'object' && sectionData !== null) {
      const hasContent = checkObjectHasContentInsideSection(sectionData);
      if (hasContent) {
        sectionsWithContent.push(key);
      }
    } else {
      sectionsWithContent.push(key);
    }
  }

  // Build the formatted message
  let message = '';
  if (sectionsWithContent.length > 0) {
    const sectionsList = sectionsWithContent.join(', ');
    message = `System Message: A report summary was displayed to the user. Use this list to suggest follow up questions to the user: ${sectionsList}. DO NOT trigger a get_specific_report_section function call with this message.`;
  }

  return { permalink: permalinkValue, message };
};

const createFunctionCallResultForTriggerSearch = (
  functionCallId: string,
  output: any,
  permalink: string | null,
  searchType: string | null,
  rawData: GenericReportResponse | null,
  teaser: TeaserRecord[] | null = null,
  params: PersonParamsFunctionArguments | null = null
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output: JSON.stringify(output),
  permalink: permalink || undefined,
  searchType: searchType || undefined,
  reportData: rawData || undefined,
  teaser: teaser || undefined,
  params: params || undefined,
});

const createFunctionCallResultForGetSpecificReportSection = (
  functionCallId: string,
  report_section_data: string,
  rawData?: GenericReportResponse,
  permalink?: string,
  searchType?: string
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output: report_section_data,
  permalink,
  searchType,
  reportData: rawData,
});

const createFunctionCallResultForCustomerServiceContact = (
  functionCallId: string
): ProcessedFunctionCallResponse => ({
  type: FUNCTION_CALL_OUTPUT,
  call_id: functionCallId,
  output:
    'Repeat this specific message: "Please refer to the customer service contact information provided."',
});

export const fetchReportByPermalink = async (
  permalink: string,
  searchType: string
): Promise<{ data: any; rawData: GenericReportResponse | null }> => {
  try {
    const coreResult = await fetchReportByPermalinkInLine(
      permalink,
      searchType
    );
    let processedResult: any = coreResult;

    const reportProcessor =
      REPORT_PROCESSORS[searchType as keyof typeof REPORT_PROCESSORS];

    const processedReportResult = reportProcessor
      ? reportProcessor(coreResult as any, permalink)
      : null;
    if (processedReportResult) {
      processedResult = processedReportResult;
    }
    return { data: processedResult, rawData: coreResult };
  } catch (error) {
    if (error instanceof Error) {
      return { data: error.message, rawData: null };
    }
    return { data: GENERIC_ERROR_MESSAGE, rawData: null };
  }
};

const parseFunctionCallArguments = (
  functionCallArgs: string
): ToolCallParameters | null => {
  try {
    return JSON.parse(functionCallArgs || '{}') as ToolCallParameters;
  } catch (_error) {
    return null;
  }
};

export const mapResponsesAPIResponseToProcessedResponse = (
  response: OpenAI.Responses.Response
): GenericLLMResponse => {
  const contextId = (response as any).context_id || '';

  if (response && response.output && response.output.length > 0) {
    const functionCalls = response.output.filter(
      (output) => output.type === FUNCTION_CALL_TYPE
    );

    if (functionCalls.length > 0) {
      const functionCallsData: FunctionCallResponse[] = functionCalls.map(
        (functionCall) => {
          const functionArgs = parseFunctionCallArguments(
            functionCall.arguments
          );
          return {
            name: functionCall.name ?? '',
            arguments: functionArgs ?? {},
            call_id: functionCall.call_id,
            type: functionCall.type ?? '',
            status: functionCall.status ?? '',
          };
        }
      );
      return {
        context_id: contextId,
        data: [null, functionCallsData],
        response_id: response.id,
      };
    }

    const genericLLMResponse: GenericLLMResponse = {
      context_id: contextId,
      data: [extractOutputText(response.output), null],
      sources: extractOutputAnnotations(response.output),
      response_id: response.id,
    };

    return genericLLMResponse;
  }

  const genericLLMResponse: GenericLLMResponse = {
    context_id: contextId,
    data: [UNSUPPORTED_SEARCH_ERROR_MESSAGE, null],
    response_id: response.id,
  };
  return genericLLMResponse;
};

const findFirstOutputMessage = (output: any[]) => {
  return output.find(
    (output) =>
      output.type === MESSAGE &&
      output.content &&
      output.content.length > 0 &&
      output.content[0]?.type === OUTPUT_TEXT
  );
};

const extractOutputAnnotations = (output: any[]): any[] => {
  if (!output || !Array.isArray(output) || output.length === 0) {
    return [];
  }

  const firstOutputMessage = findFirstOutputMessage(output);
  if (
    !firstOutputMessage ||
    !firstOutputMessage.content ||
    !Array.isArray(firstOutputMessage.content) ||
    firstOutputMessage.content.length === 0
  ) {
    return [];
  }

  const firstContent = firstOutputMessage.content[0];
  if (!firstContent || !firstContent.annotations) {
    return [];
  }

  return Array.isArray(firstContent.annotations)
    ? firstContent.annotations
    : [];
};

export const extractOutputText = (
  output: any[],
  fallbackMessage: string = GENERIC_ERROR_MESSAGE
): string => {
  if (!output || output.length === 0) {
    return fallbackMessage;
  }

  const firstOutputMessage = findFirstOutputMessage(output);
  if (firstOutputMessage) {
    return firstOutputMessage.content[0].text || fallbackMessage;
  }

  return fallbackMessage;
};

export const buildUserRequest = (
  message: BaseMessage,
  lastContextId?: string,
  useWebSearch?: boolean
): GenericLLMRequest => ({
  items: [
    {
      type: null,
      call_id: null,
      output: null,
      role: ROLES.USER,
      content: message.content,
      metadata: null,
    },
  ],
  context_id: lastContextId || null,
  useWebSearch,
});

/**
 * Builds a system message request for court opinion card click
 * This message is sent to smartsearchbot to store the cluster_id for later restoration
 * The message is formatted as a system instruction that should not generate any response
 * @param clusterId - The cluster ID of the court opinion
 * @param lastContextId - The current context ID from the conversation
 * @param opinionSummaryContextId - Optional context_id from the opinion summary response
 * @param caseName - Optional case name/title to store in metadata for UI display
 * @returns GenericLLMRequest with system message
 */
export const buildCourtOpinionSystemMessageRequest = (
  clusterId: number,
  lastContextId?: string,
  opinionSummaryContextId?: string,
  caseName?: string
): GenericLLMRequest => {
  const metadataObj: {
    cluster_id: number;
    type: string;
    is_system_message: boolean;
    opinion_summary_context_id?: string;
    case_name?: string;
  } = {
    cluster_id: clusterId,
    type: 'court_opinion_selection',
    is_system_message: true,
  };

  if (opinionSummaryContextId) {
    metadataObj.opinion_summary_context_id = opinionSummaryContextId;
  }

  if (caseName) {
    metadataObj.case_name = caseName;
  }

  const metadataString = JSON.stringify(metadataObj);
  // Format for inline: remove outer quotes and format nicely
  const inlineMetadata = `{cluster_id: ${clusterId}, type: 'court_opinion_selection'${
    opinionSummaryContextId
      ? `, opinion_summary_context_id: '${opinionSummaryContextId}'`
      : ''
  }${
    caseName
      ? `, case_name: '${caseName.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
      : ''
  }}`;

  const request = {
    items: [
      {
        type: null,
        call_id: null,
        output: null,
        role: ROLES.ASSISTANT, // Use ASSISTANT role for system messages to the API
        content: `[SYSTEM MESSAGE - DO NOT RESPOND] User selected court opinion card. [{metadata: ${inlineMetadata}}] Store this cluster_id in conversation context for UI state restoration. This is an internal metadata update only. Do not generate any response, function calls, or user-facing messages.`,
        metadata: metadataString,
      },
    ],
    context_id: lastContextId || null,
  };

  return request;
};

export const buildLLMOrientedRequest = (
  request: GenericLLMRequest
): ReponsesAPIRequestProps | LLMRequestItem => {
  if (USE_OPENAI_LOCAL_CONFIGURATION) {
    const itemsToSend: InputItem[] = [];

    for (const item of request.items) {
      if (item.type === FUNCTION_CALL_OUTPUT && item.call_id && item.output) {
        itemsToSend.push({
          type: item.type,
          call_id: item.call_id,
          output: item.output,
        });
      } else {
        itemsToSend.push({
          role: ROLES.USER,
          content: item.content || '',
        });
      }
    }
    return {
      input: itemsToSend,
      previous_response_id: request.context_id || undefined,
    };
  }
  // If not using local configuration, we need to build the request for the LLM

  const functionCallOutputs: LLMFunctionCallOutput[] = [];
  const prompt: string[] = [];

  for (const item of request.items) {
    if (item.type === FUNCTION_CALL_OUTPUT && item.call_id && item.output) {
      const functionCallOutput: LLMFunctionCallOutput = {
        id: item.call_id,
        content: item.output,
        metadata: item.metadata || '',
      };
      functionCallOutputs.push(functionCallOutput);
    } else {
      prompt.push(item.content || '');
    }
  }

  const requestToSend: LLMRequestItem = {
    ...(prompt.length > 0 && { prompt: prompt.toString() }),
    ...(functionCallOutputs.length > 0 && {
      function_call_outputs: functionCallOutputs,
    }),
    contextId: request.context_id || undefined,
  };

  return requestToSend;
};

// Helper function to extract attached-data from message content
export const extractAttachedData = (
  content: string
): {
  cleanContent: string;
  attachedData: { permalink?: string; reportType?: string } | null;
} => {
  // Match [attached-data: {permalink: abc123, report-Type: person}] or similar variations
  const attachedDataRegex = /\[attached-data:\s*\{([^}]+)\}\]/i;
  const match = content.match(attachedDataRegex);

  if (!match) {
    return { cleanContent: content, attachedData: null };
  }

  const cleanContent = content.replace(attachedDataRegex, '').trim();
  const attachedDataString = match[1];

  // Parse attached-data: {permalink: abc123, report-Type: person}
  // Handle both "report-Type" and "reportType" variations
  const permalinkMatch = attachedDataString.match(/permalink:\s*([^\s,}]+)/i);
  const reportTypeMatch = attachedDataString.match(
    /report-?Type:\s*([^\s,}]+)/i
  );

  const attachedData: { permalink?: string; reportType?: string } = {};
  if (permalinkMatch) {
    attachedData.permalink = permalinkMatch[1].trim();
  }
  if (reportTypeMatch) {
    attachedData.reportType = reportTypeMatch[1].trim();
  }

  return {
    cleanContent,
    attachedData: Object.keys(attachedData).length > 0 ? attachedData : null,
  };
};

/**
 * Extracts hidden metadata from message content in the format [{metadata: {...}}]
 * @param content - Message content that may contain hidden metadata
 * @returns Extracted metadata object or null if not found
 */
export const extractHiddenMetadata = (
  content: string
): {
  cluster_id?: number;
  type?: string;
  opinion_summary_context_id?: string;
  case_name?: string;
} | null => {
  // Match [{metadata: {cluster_id: 123, type: 'court_opinion_selection', opinion_summary_context_id: 'abc'}}]
  // Try to match the metadata pattern - handle both simple and complex cases
  const metadataMatch = content.match(/\[\{metadata:\s*\{([^}]+)\}\}\]/i);

  if (!metadataMatch) {
    return null;
  }

  try {
    // Parse the metadata string - handle both JSON format and simplified format
    let metadataString = metadataMatch[1];

    // Replace single quotes with double quotes for JSON parsing
    metadataString = metadataString.replace(/'/g, '"');

    // Add quotes around keys if they don't have them (e.g., cluster_id: -> "cluster_id":)
    // This handles formats like: cluster_id: 7017480, type: "court_opinion_selection"
    // Match keys at start of string or after comma/opening brace
    metadataString = metadataString.replace(
      /(^|[{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g,
      '$1"$2":'
    );

    // Ensure proper JSON format
    if (!metadataString.startsWith('{')) {
      metadataString = `{${metadataString}}`;
    }
    const parsedMetadata = JSON.parse(metadataString);

    // Return only relevant fields
    const result: {
      cluster_id?: number;
      type?: string;
      opinion_summary_context_id?: string;
      case_name?: string;
    } = {};

    if (parsedMetadata.cluster_id) {
      result.cluster_id =
        typeof parsedMetadata.cluster_id === 'number'
          ? parsedMetadata.cluster_id
          : parseInt(parsedMetadata.cluster_id, 10);
    }
    if (parsedMetadata.type) {
      result.type = parsedMetadata.type;
    }
    if (parsedMetadata.opinion_summary_context_id) {
      result.opinion_summary_context_id =
        parsedMetadata.opinion_summary_context_id;
    }
    if (parsedMetadata.case_name) {
      result.case_name = parsedMetadata.case_name;
    }

    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
};

/**
 * Checks if a message content is a generic response to a system message
 * @param content - The message content to check
 * @param previousWasSystemMessage - Optional flag indicating if the previous message was a system message
 * @returns true if the message is a generic system response that should be filtered
 */
const isGenericSystemResponse = (
  content: string,
  previousWasSystemMessage = false
): boolean => {
  const lowerContent = content.toLowerCase();
  const hasSystemMessageKeywords =
    lowerContent.includes('internal metadata') ||
    lowerContent.includes('metadata update');

  const hasAcknowledgmentKeywords =
    lowerContent.includes('understood') ||
    lowerContent.includes('acknowledge') ||
    lowerContent.includes('noted') ||
    lowerContent.includes('got it') ||
    lowerContent.includes('understand');

  const hasGenericAssistancePhrases =
    lowerContent.includes('let me know') ||
    lowerContent.includes('further assistance') ||
    lowerContent.includes('how i can assist') ||
    lowerContent.includes('how can i assist') ||
    lowerContent.includes('anything else') ||
    lowerContent.includes('need anything else') ||
    lowerContent.includes('feel free to ask') ||
    lowerContent.includes('feel free');

  return (
    (hasSystemMessageKeywords &&
      (hasAcknowledgmentKeywords || hasGenericAssistancePhrases)) ||
    (content.length < 150 &&
      hasAcknowledgmentKeywords &&
      hasGenericAssistancePhrases) ||
    (previousWasSystemMessage &&
      content.length < 150 &&
      hasAcknowledgmentKeywords &&
      hasGenericAssistancePhrases)
  );
};

export const mapConversationMessagesToMessages = (
  messages: ConversationMessage[],
  generatedReports?: Record<string, GeneratedReport>
): Message[] => {
  let savedMetadata: string | null = null;
  const resultMessages: Message[] = [];
  const processedIndices = new Set<number>();

  messages.forEach((message, index) => {
    // Skip if already processed
    if (processedIndices.has(index)) {
      return;
    }

    // Check for hidden metadata in message content first
    const hiddenMetadata = extractHiddenMetadata(message.message || '');

    // If this is a USER message with court opinion metadata, create assistant message
    // No need to pair with a user message - just create the COURT_OPINION_PAGE directly
    if (
      hiddenMetadata &&
      hiddenMetadata.type === 'court_opinion_selection' &&
      message.role === ROLES.USER
    ) {
      // Check if we already created a COURT_OPINION_PAGE message for this cluster_id
      const alreadyExists = resultMessages.some(
        (msg) =>
          msg.content === 'COURT_OPINION_PAGE' &&
          msg.courtOpinionClusterId === hiddenMetadata.cluster_id
      );

      // Also check if we already created a user message with this case_name
      const alreadyHasUserMessage = hiddenMetadata.case_name
        ? resultMessages.some(
            (msg) =>
              msg.role === ROLES.USER &&
              msg.content === hiddenMetadata.case_name
          )
        : false;

      if (!alreadyExists) {
        // If case_name is available in metadata, add it as a user message first
        // This mimics the behavior of addMessageDirectly
        // BUT only if we haven't already added a user message with this case_name
        // IMPORTANT: Don't add any properties that could trigger teaser loading
        if (hiddenMetadata.case_name && !alreadyHasUserMessage) {
          const userMessage: Message = {
            content: hiddenMetadata.case_name,
            role: ROLES.USER,
            // Explicitly don't set params, searchType, or any teaser-related properties
            // This prevents the teaser from being triggered for court opinion case names
          };
          resultMessages.push(userMessage);
        }

        // Create assistant message with COURT_OPINION_PAGE content
        // Explicitly set isLoadingTeaser to false and ensure no teaser properties
        const assistantMessage: Message = {
          content: 'COURT_OPINION_PAGE',
          role: ROLES.ASSISTANT,
          courtOpinionClusterId: hiddenMetadata.cluster_id,
          opinionSummaryContextId: hiddenMetadata.opinion_summary_context_id,
          courtOpinionCaseName: hiddenMetadata.case_name,
          isLoadingTeaser: false, // Explicitly set to false to prevent teaser loading
          teaser: undefined, // Ensure no teaser is set
        };
        resultMessages.push(assistantMessage);
      }

      // Clear savedMetadata after processing court opinion message
      // This prevents teaser metadata from previous messages being applied to subsequent messages
      savedMetadata = null;

      processedIndices.add(index);

      // Also skip the next assistant message if it's a generic response to the system message
      if (messages[index + 1]?.role === ROLES.ASSISTANT) {
        const nextMessageContent =
          messages[index + 1].responses?.[0] ||
          messages[index + 1].message ||
          '';
        if (isGenericSystemResponse(nextMessageContent)) {
          processedIndices.add(index + 1);
        }
      }
      // Skip processing the system message (it's internal)
      return;
    }

    // Also check for system message indicators in USER messages
    // BUT if this is a regular user message (not a system message), process it normally
    if (message.role === ROLES.USER) {
      const messageContent = message.message || '';
      const isSystemMessage =
        messageContent.includes('[SYSTEM MESSAGE') ||
        messageContent.includes('DO NOT RESPOND') ||
        (messageContent.includes('internal metadata') &&
          messageContent.includes('Store this')) ||
        extractHiddenMetadata(messageContent)?.type ===
          'court_opinion_selection';

      if (isSystemMessage) {
        // This is a system message, skip it
        return;
      }
      // Otherwise, continue processing - this is a regular user message that should be shown
    }

    if (hiddenMetadata) {
      try {
        // Convert hidden metadata to JSON string format for consistency
        savedMetadata = JSON.stringify(hiddenMetadata);
      } catch {
        // If conversion fails, continue without it
      }
    }

    // Check if this message has function_call_outputs
    // This can happen in both USER and ASSISTANT messages
    if (
      message.function_call_outputs &&
      message.function_call_outputs.length > 0
    ) {
      // Look for metadata in any of the function_call_outputs
      for (const output of message.function_call_outputs) {
        if (output.metadata) {
          // Merge with hidden metadata if it exists
          if (hiddenMetadata) {
            try {
              const parsedOutputMetadata = JSON.parse(output.metadata);
              const mergedMetadata = {
                ...parsedOutputMetadata,
                ...hiddenMetadata,
                type: hiddenMetadata.type || parsedOutputMetadata.type,
              };
              savedMetadata = JSON.stringify(mergedMetadata);
            } catch {
              savedMetadata = output.metadata;
            }
          } else {
            savedMetadata = output.metadata;
          }
          break; // Use the first metadata found
        }
      }

      // For USER messages with function_call_outputs, try to extract report data
      // This is for phone, property, email, vehicle reports
      if (message.role === ROLES.USER) {
        for (const output of message.function_call_outputs) {
          if (output.content) {
            try {
              const parsedContent = JSON.parse(output.content);
              if (parsedContent.permalink && parsedContent.data) {
                // Extract search type from the data or try to determine it
                // For phone reports, we can check if there's phoneNumber in data
                let searchType: string | undefined;

                if (parsedContent.data.phoneNumber) {
                  searchType = SEARCH_TYPES.PHONE;
                } else if (parsedContent.data.email) {
                  searchType = SEARCH_TYPES.EMAIL;
                } else if (
                  parsedContent.data.address ||
                  parsedContent.data.propertyAddress
                ) {
                  searchType = SEARCH_TYPES.PROPERTY;
                } else if (
                  parsedContent.data.vin ||
                  parsedContent.data.vehicleVin
                ) {
                  searchType = SEARCH_TYPES.VEHICLE;
                }

                if (searchType && parsedContent.permalink) {
                  // Check if report exists in generatedReports
                  const existingReport =
                    generatedReports?.[parsedContent.permalink];

                  // Create assistant message with report or loading state
                  const assistantMessage: Message = existingReport
                    ? {
                        content: '',
                        role: ROLES.ASSISTANT,
                        reports: [
                          {
                            reportData: existingReport.reportData,
                            type: existingReport.searchType,
                            permalink: existingReport.permalink,
                          },
                        ],
                        executedFunctionCall: TRIGGER_SEARCH,
                      }
                    : {
                        content: '',
                        role: ROLES.ASSISTANT,
                        permalink: parsedContent.permalink,
                        searchType,
                        isLoadingReport: true,
                        executedFunctionCall: TRIGGER_SEARCH,
                      };

                  resultMessages.push(assistantMessage);
                  // Don't add user message, it's internal
                  return;
                }
              }
            } catch {
              // Not valid JSON, continue
            }
          }
        }
      }

      // This message has function calls, so it will be mapped to null
      // The metadata will be applied to the next assistant message without function calls
      const previousMessageForFunctionCall =
        index > 0 ? messages[index - 1] : undefined;
      const mappedMessage = mapConversationMessageToMessage(
        message,
        null,
        previousMessageForFunctionCall
      );
      if (mappedMessage) {
        resultMessages.push(mappedMessage);
      }
      return;
    }

    // Map the message, applying saved metadata if this is an assistant message without function calls
    // Pass previous message to help detect generic responses to system messages
    const previousMessage = index > 0 ? messages[index - 1] : undefined;

    // Check if this message is a court opinion message before applying metadata
    // If it is, don't apply teaser metadata from previous messages
    const isCourtOpinionMessage =
      hiddenMetadata?.cluster_id ||
      message.message?.includes('court_opinion_selection') ||
      (message.responses?.[0] &&
        message.responses[0].includes('COURT_OPINION_PAGE'));

    // If this is a court opinion message, clear savedMetadata to prevent applying teaser metadata
    const metadataToApply = isCourtOpinionMessage ? null : savedMetadata;

    const mappedMessage = mapConversationMessageToMessage(
      message,
      metadataToApply,
      previousMessage
    );

    // Skip if mappedMessage is null (filtered out)
    if (!mappedMessage) {
      return;
    }

    // If we found hidden metadata with cluster_id, ensure content is set to COURT_OPINION_PAGE
    // BUT only if we haven't already created a COURT_OPINION_PAGE message from a user system message
    // Check if we already have a COURT_OPINION_PAGE message with the same cluster_id
    const alreadyHasCourtOpinionMessage = hiddenMetadata?.cluster_id
      ? resultMessages.some(
          (msg) =>
            msg.content === 'COURT_OPINION_PAGE' &&
            msg.courtOpinionClusterId === hiddenMetadata.cluster_id
        )
      : false;

    if (
      hiddenMetadata &&
      hiddenMetadata.cluster_id &&
      mappedMessage &&
      !alreadyHasCourtOpinionMessage
    ) {
      if (!mappedMessage.courtOpinionClusterId) {
        mappedMessage.courtOpinionClusterId = hiddenMetadata.cluster_id;
      }
      if (
        hiddenMetadata.opinion_summary_context_id &&
        !mappedMessage.opinionSummaryContextId
      ) {
        mappedMessage.opinionSummaryContextId =
          hiddenMetadata.opinion_summary_context_id;
      }
      if (hiddenMetadata.case_name && !mappedMessage.courtOpinionCaseName) {
        mappedMessage.courtOpinionCaseName = hiddenMetadata.case_name;
      }
      if (
        hiddenMetadata.type === 'court_opinion_selection' &&
        mappedMessage.content !== 'COURT_OPINION_PAGE'
      ) {
        mappedMessage.content = 'COURT_OPINION_PAGE';
      }

      // Clear savedMetadata after processing court opinion message
      // This prevents teaser metadata from being applied to this or subsequent messages
      if (hiddenMetadata && hiddenMetadata.cluster_id) {
        savedMetadata = null;
      }
    }

    // Process attached-data for user messages (only for person, obituary, and teaser-derived types)
    if (mappedMessage && mappedMessage.role === ROLES.USER) {
      const { cleanContent, attachedData } = extractAttachedData(
        mappedMessage.content
      );

      if (attachedData?.permalink && attachedData?.reportType) {
        // Only process attached-data for person, obituary, and teaser-derived types
        // For phone, property, email, vehicle - just clean the content but don't create assistant message
        const reportType = attachedData.reportType.toLowerCase();
        const shouldProcessAttachedData =
          reportType === SEARCH_TYPES.PERSON ||
          reportType === SEARCH_TYPES.OBITUARY;

        // Always update user message with clean content
        mappedMessage.content = cleanContent;

        if (shouldProcessAttachedData) {
          // Check if report exists in generatedReports
          const existingReport = generatedReports?.[attachedData.permalink];

          // Create assistant message with report or loading state
          const assistantMessage: Message = existingReport
            ? {
                content: '',
                role: ROLES.ASSISTANT,
                reports: [
                  {
                    reportData: existingReport.reportData,
                    type: existingReport.searchType,
                    permalink: existingReport.permalink,
                  },
                ],
                executedFunctionCall: TRIGGER_SEARCH,
              }
            : {
                content: '',
                role: ROLES.ASSISTANT,
                permalink: attachedData.permalink,
                searchType: attachedData.reportType,
                isLoadingReport: true,
                executedFunctionCall: TRIGGER_SEARCH,
              };

          resultMessages.push(mappedMessage);
          resultMessages.push(assistantMessage);
          return;
        }
        // For other types (phone, property, email, vehicle), just add the cleaned message
        // and continue with normal processing
      }
    }

    // If this is an assistant message without function calls that received metadata,
    // clear savedMetadata so it's only applied once
    if (
      mappedMessage &&
      mappedMessage.role === ROLES.ASSISTANT &&
      savedMetadata !== null
    ) {
      savedMetadata = null;
    }

    if (mappedMessage) {
      resultMessages.push(mappedMessage);
    }
  });

  return resultMessages;
};

export const mapConversationMessageToMessage = (
  message: ConversationMessage,
  metadata: string | null = null,
  previousMessage?: ConversationMessage
): Message | null => {
  if (message.role === ROLES.ASSISTANT) {
    if (
      !message.function_call_outputs ||
      message.function_call_outputs.length === 0
    ) {
      // Use responses[0] if available, otherwise fall back to message
      const content = message.responses?.[0] || message.message || '';
      // Skip messages with empty content
      if (!content.trim()) {
        return null;
      }
      const result: Message = {
        content,
        role: ROLES.ASSISTANT,
        context_id: undefined,
      };

      // Check if this is a court opinion system message by checking content
      // Use extractHiddenMetadata helper function for consistency
      const hiddenMetadata = extractHiddenMetadata(content);
      if (hiddenMetadata && hiddenMetadata.type === 'court_opinion_selection') {
        if (hiddenMetadata.cluster_id) {
          result.courtOpinionClusterId = hiddenMetadata.cluster_id;
        }
        // Store opinion_summary_context_id if available
        if (hiddenMetadata.opinion_summary_context_id) {
          result.opinionSummaryContextId =
            hiddenMetadata.opinion_summary_context_id;
        }
        // Don't display system messages to user - they're internal only
        return null;
      }

      // Check if this is a generic response to a system message
      // Filter out responses like "Understood. The internal metadata update has been noted."
      const previousWasSystemMessage = previousMessage
        ? previousMessage.role === ROLES.USER &&
          (previousMessage.message?.includes('[SYSTEM MESSAGE') ||
            previousMessage.message?.includes('DO NOT RESPOND') ||
            extractHiddenMetadata(previousMessage.message || '')?.type ===
              'court_opinion_selection')
        : false;

      if (isGenericSystemResponse(content, previousWasSystemMessage)) {
        return null;
      }

      // Fallback: try old format extraction if extractHiddenMetadata didn't find anything
      const fallbackMatch = content.match(/cluster_id:\s*(\d+)/i);
      if (fallbackMatch && content.includes('[SYSTEM MESSAGE')) {
        const clusterId = parseInt(fallbackMatch[1], 10);
        if (!isNaN(clusterId)) {
          result.courtOpinionClusterId = clusterId;
          return null;
        }
      }

      // Apply metadata if available - restore all metadata values
      if (metadata) {
        try {
          const parsedMetadata = JSON.parse(metadata);

          // Check if this is a court opinion message BEFORE applying metadata
          // Check content, hiddenMetadata, and parsedMetadata for court opinion indicators
          const isCourtOpinionFromContent =
            content === 'COURT_OPINION_PAGE' ||
            content.includes('court_opinion_selection');
          const isCourtOpinionFromMetadata =
            !!parsedMetadata.cluster_id &&
            parsedMetadata.type === 'court_opinion_selection';
          const isCourtOpinionFromHidden =
            hiddenMetadata?.cluster_id &&
            hiddenMetadata.type === 'court_opinion_selection';
          const isCourtOpinionMessage =
            isCourtOpinionFromContent ||
            isCourtOpinionFromMetadata ||
            isCourtOpinionFromHidden;

          // Restore basic fields
          if (parsedMetadata.permalink) {
            result.permalink = parsedMetadata.permalink;
          }
          if (parsedMetadata.searchType) {
            result.searchType = parsedMetadata.searchType;
            if (parsedMetadata.hasTeaser) {
              result.teaserSearchType = parsedMetadata.searchType;
            }
          }
          if (parsedMetadata.context_id) {
            result.context_id = parsedMetadata.context_id;
          }
          if (parsedMetadata.response_id) {
            result.response_id = parsedMetadata.response_id;
          }

          // Restore params for person searches
          if (parsedMetadata.params) {
            result.params = parsedMetadata.params;
          }

          // Restore executedFunctionCall if present, or set to TRIGGER_SEARCH for person searches with params
          if (parsedMetadata.executedFunctionCall) {
            result.executedFunctionCall = parsedMetadata.executedFunctionCall;
          } else if (
            parsedMetadata.searchType === 'person' &&
            parsedMetadata.params
          ) {
            result.executedFunctionCall = TRIGGER_SEARCH;
          }

          // Restore court opinion cluster ID for UI restoration FIRST
          // This needs to happen before checking for teaser to prevent teaser from being loaded
          if (
            parsedMetadata.cluster_id &&
            parsedMetadata.type === 'court_opinion_selection'
          ) {
            result.courtOpinionClusterId = parsedMetadata.cluster_id;
            // Restore opinion_summary_context_id if available
            if (parsedMetadata.opinion_summary_context_id) {
              result.opinionSummaryContextId =
                parsedMetadata.opinion_summary_context_id;
            }
            // Restore case_name if available
            if (parsedMetadata.case_name) {
              result.courtOpinionCaseName = parsedMetadata.case_name;
            }
            // Always set content to COURT_OPINION_PAGE when we have court opinion metadata
            result.content = 'COURT_OPINION_PAGE';
          }

          // Mark that teaser needs to be loaded if hasTeaser is true (for any teaser-capable search type)
          // BUT only if this is NOT a court opinion message (court opinion messages shouldn't have teasers)
          // Check AFTER restoring court opinion metadata so we can properly detect court opinion messages
          const isCourtOpinionContent =
            result.content === 'COURT_OPINION_PAGE' ||
            !!result.courtOpinionClusterId ||
            !!parsedMetadata.cluster_id ||
            isCourtOpinionMessage;

          const normalizedSearchType = parsedMetadata.searchType
            ?.toLowerCase()
            ?.replace(/_/g, '');
          const teaserSearchTypes = [
            'person',
            'obituary',
            'ancestor',
            'contact',
            'courtcases',
            'courtopinions',
          ];
          const hasNameParams =
            parsedMetadata.params?.first_name &&
            parsedMetadata.params?.last_name;
          const hasCaseName =
            parsedMetadata.params?.case_name ||
            (parsedMetadata as { case_name?: string }).case_name;
          const isTeaserType =
            normalizedSearchType &&
            teaserSearchTypes.includes(normalizedSearchType);
          const canLoadTeaser =
            isTeaserType &&
            (hasNameParams ||
              (['courtcases', 'courtopinions'].includes(normalizedSearchType) &&
                hasCaseName));

          if (
            parsedMetadata.hasTeaser &&
            !result.teaser &&
            !isCourtOpinionContent &&
            canLoadTeaser
          ) {
            result.isLoadingTeaser = true;
          }

          // Mark as loading report if we have permalink and searchType but no reports yet
          if (
            parsedMetadata.permalink &&
            parsedMetadata.searchType &&
            !result.reports
          ) {
            result.isLoadingReport = true;
          }
        } catch {
          // If metadata is not valid JSON, ignore it
        }
      }

      // Also check if content is COURT_OPINION_PAGE and try to extract cluster_id from message content
      // This handles cases where the message was restored but cluster_id needs to be extracted
      // Also check if we have cluster_id but content is not COURT_OPINION_PAGE yet
      if (
        (content === 'COURT_OPINION_PAGE' || !result.courtOpinionClusterId) &&
        !result.courtOpinionClusterId
      ) {
        // Use extractHiddenMetadata helper function for consistency
        const messageHiddenMetadata = extractHiddenMetadata(
          message.message || ''
        );
        if (messageHiddenMetadata && messageHiddenMetadata.cluster_id) {
          result.courtOpinionClusterId = messageHiddenMetadata.cluster_id;
          if (messageHiddenMetadata.opinion_summary_context_id) {
            result.opinionSummaryContextId =
              messageHiddenMetadata.opinion_summary_context_id;
          }
          if (messageHiddenMetadata.case_name) {
            result.courtOpinionCaseName = messageHiddenMetadata.case_name;
          }
          // Set content to COURT_OPINION_PAGE if we found court opinion metadata
          if (messageHiddenMetadata.type === 'court_opinion_selection') {
            result.content = 'COURT_OPINION_PAGE';
          }
        } else {
          // Fallback: Try to extract from message content if it contains cluster_id
          const clusterIdMatch = message.message?.match(/cluster_id:\s*(\d+)/i);
          if (clusterIdMatch) {
            const clusterId = parseInt(clusterIdMatch[1], 10);
            if (!isNaN(clusterId)) {
              result.courtOpinionClusterId = clusterId;
              result.content = 'COURT_OPINION_PAGE';
            }
          }
        }
      }

      // If we have courtOpinionClusterId but content is not COURT_OPINION_PAGE, set it
      if (
        result.courtOpinionClusterId &&
        result.content !== 'COURT_OPINION_PAGE'
      ) {
        result.content = 'COURT_OPINION_PAGE';
      }

      return result;
    }
    return null;
  } else if (message.role === ROLES.USER) {
    if (
      message.function_call_outputs &&
      message.function_call_outputs.length > 0
    ) {
      return null;
    }

    const messageContent = message.message.trim();

    // Check if this is a court opinion system message (hidden metadata in user message)
    const hiddenMetadata = extractHiddenMetadata(messageContent);
    if (hiddenMetadata && hiddenMetadata.type === 'court_opinion_selection') {
      // This is a system message, filter it out (don't show to user)
      return null;
    }

    // Also check for system message indicators in the content
    if (
      messageContent.includes('[SYSTEM MESSAGE') ||
      messageContent.includes('DO NOT RESPOND') ||
      (messageContent.includes('internal metadata') &&
        messageContent.includes('Store this'))
    ) {
      // This is a system message, filter it out
      return null;
    }

    // Check if this is a message generated from teaser selection
    // Format can be: "**User prompt:** {...}" or directly {...}
    let contentToCheck = messageContent;
    const userPromptMatch = messageContent.match(
      /^\*\*User prompt:\*\*\s*(.+)$/
    );
    if (userPromptMatch) {
      contentToCheck = userPromptMatch[1].trim();
    }

    // Try to parse as JSON to detect teaser selection messages
    try {
      const parsedContent = JSON.parse(contentToCheck);
      if (parsedContent.permalink && parsedContent.data) {
        const personName = parsedContent.data?.personName || 'this person';
        const friendlyMessage = `Tell me about ${personName}`;
        return {
          content: friendlyMessage,
          role: ROLES.USER,
          teaserSelectionMessage: friendlyMessage,
        };
      }
    } catch {
      // Not JSON, continue with normal processing
    }

    // Filter out internal permalink messages that don't have the teaser format
    // But allow messages with attached-data
    if (
      messageContent.includes('permalink') &&
      !messageContent.includes('data') &&
      !messageContent.includes('attached-data')
    ) {
      return null;
    }

    return {
      content: messageContent.replace('**User prompt:**', '').trim() || '',
      role: ROLES.USER,
    };
  }
  return null;
};
